import React, { useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Button, Alert } from './ui';
import { XMarkIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import groupService from '../services/groupService';
import BatchUploadModal from './BatchUploadModal';
import PublicGroupSearchCard from './PublicGroupSearchCard';

const AddGroupsModal = ({ isOpen, onClose, onProcessStarted }) => {
  const [links, setLinks] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoProcess, setAutoProcess] = useState(true);
  const [importResults, setImportResults] = useState(null);
  const submitting = useRef(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [linksArray, setLinksArray] = useState([]);
  const [isPublicBatchProcessing, setIsPublicBatchProcessing] = useState(false);
  const queryClient = useQueryClient();

  const BATCH_THRESHOLD = 500; // Use batch processing for 500+ links

  // Clear state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setLinks('');
      setError(null);
      setSuccess(null);
      setAutoProcess(true);
      setImportResults(null);
      setIsPublicBatchProcessing(false);
    }
  }, [isOpen]);

  // Add groups mutation
  const addGroupsMutation = useMutation(
    (linksData) => groupService.addBulkGroups(linksData),
    {
      onSuccess: (data, variables) => {
        if (variables.autoProcess) {
          setSuccess(`Обработано ссылок: ${data.total_processed ?? variables.links.length}. В очереди: ${data.queued ?? 0}. Включите обработку на вкладке «Автообработка», если она на паузе.`);
          setImportResults((data.results || []).map((item, index) => ({ ...item, input: item.input || variables.links[index] })));
          queryClient.invalidateQueries('groups');
          queryClient.invalidateQueries('groupLifecycle');
          return;
        }
        setSuccess(`Обработано ссылок: ${data.total_processed}. Добавлено: ${data.added}, уже в базе: ${data.skipped_existing}, некорректных: ${data.invalid || 0}`);
        setLinks('');
        
        // Refresh groups list
        queryClient.invalidateQueries('groups');
        
        // If there's a process ID, notify parent
        if (data.processId && onProcessStarted) {
          onProcessStarted(data.processId);
        }
        
        // Close modal after success
        setTimeout(() => {
          onClose();
        }, 2000);
      },
      onError: (err) => {
        setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Не удалось добавить группы');
      },
      onSettled: () => { submitting.current = false; }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting.current || addGroupsMutation.isLoading || isPublicBatchProcessing) return;
    
    if (!links.trim()) {
      setError('Введите ссылки или имена групп');
      return;
    }

    // Split links by newlines and filter out empty lines
    const linkArray = links
      .split('\n')
      .map(link => link.trim())
      .filter(Boolean);

    if (linkArray.length === 0) {
      setError('Введите корректные ссылки или имена групп');
      return;
    }

    setError(null);
    setSuccess(null);
    setImportResults(null);

    if (autoProcess && linkArray.length > 100) {
      setError('Автоматический цикл принимает не более 100 непустых строк за раз. Разделите список на части.');
      return;
    }

    // Use batch processing for large lists
    if (!autoProcess && linkArray.length >= BATCH_THRESHOLD) {
      setLinksArray(linkArray);
      setShowBatchModal(true);
      return;
    }

    // Regular processing for smaller lists
    submitting.current = true;
    addGroupsMutation.mutate({ links: linkArray, autoProcess });
  };

  const handleClose = () => {
    if (!addGroupsMutation.isLoading && !isPublicBatchProcessing) {
      onClose();
    }
  };

  const handleBatchComplete = (result) => {
    setShowBatchModal(false);
    if (result.success) {
      setSuccess(`Пакетное добавление завершено. Добавлено: ${result.totalAdded}, уже в базе: ${result.totalExisting}, некорректных: ${result.totalInvalid}`);
      setLinks('');
      
      // Refresh groups list
      queryClient.invalidateQueries('groups');
      
      if (onProcessStarted) {
        onProcessStarted('batch-upload-completed');
      }
      
      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 3000);
    }
  };

  const getLinkCount = () => {
    if (!links.trim()) return 0;
    return links.split('\n').map(link => link.trim()).filter(Boolean).length;
  };

  const linkCount = getLinkCount();
  const willUseBatch = !autoProcess && linkCount >= BATCH_THRESHOLD;
  const resultLabels = { created: 'Добавлена', failed: 'Не сохранена', queued: 'В очереди', existing: 'Уже в системе', duplicate: 'Повтор в списке', invalid: 'Некорректная ссылка', manual: 'Нужен оператор' };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h3 className="text-lg font-medium text-gray-900">Добавить группы</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={addGroupsMutation.isLoading || isPublicBatchProcessing}
              aria-label="Закрыть окно добавления групп"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            <PublicGroupSearchCard onBatchProcessingChange={setIsPublicBatchProcessing} />

            <div>
              <p className="text-sm text-gray-600 mb-4">
                Добавьте ссылки в автоматический цикл ниже. Для немедленной подписки и привязки
                публичных групп к одному проекту используйте поиск выше.
              </p>
              
              {linkCount > 0 && (
                <div className={`p-3 rounded-md mb-4 ${
                  willUseBatch 
                    ? 'bg-orange-50 border border-orange-200' 
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <div className="flex items-center">
                    {willUseBatch && <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 mr-2" />}
                    <div className="text-sm">
                      <p className={willUseBatch ? 'text-orange-700' : 'text-blue-700'}>
                        Найдено ссылок: <strong>{linkCount.toLocaleString()}</strong>{autoProcess && ' / 100'}
                        {willUseBatch && (
                          <span className="block mt-1">
                            Большой список: пакетная обработка по 1000 ссылок
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-2">
                <strong className="text-sm text-gray-700">Поддерживаемые форматы:</strong>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                  <li>• @username (например, @mychannel)</li>
                  <li>• t.me/username (например, t.me/mychannel)</li>
                  <li>• https://t.me/username</li>
                  <li>• telegram.me/username</li>
                </ul>
              </div>
            </div>

            {error && (
              <Alert variant="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            {importResults && <ul className="max-h-60 space-y-2 overflow-y-auto text-sm" aria-label="Результаты добавления" aria-live="polite">
              {importResults.map((item, index) => <li key={index} className="break-words rounded bg-gray-50 p-2"><span className="font-medium">{item.input}</span> — {resultLabels[item.status] || 'Получен результат'}{item.reason && <p className="mt-1 text-xs text-gray-600">{item.reason}</p>}</li>)}
            </ul>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-md border border-gray-200 p-3">
                <label className="flex items-start gap-2 text-sm font-medium text-gray-800">
                  <input type="checkbox" checked={autoProcess} onChange={(event) => setAutoProcess(event.target.checked)} disabled={addGroupsMutation.isLoading || isPublicBatchProcessing} aria-describedby="auto-process-help" className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  Автоматический цикл: Basic Info → AI → подписка
                </label>
                <p id="auto-process-help" className="mt-2 text-xs text-gray-500">{autoProcess ? 'До 100 ссылок за раз. Группы попадают в очередь; запуск и пауза — на вкладке «Автообработка». Подписка — для подходящих активных проектов; остальные группы остаются в резерве.' : 'Только добавление записей в базу. Basic Info, AI-анализ и подписка запускаются отдельно.'}</p>
                {autoProcess && linkCount > 100 && <p className="mt-2 text-xs text-red-700">Слишком много ссылок для автообработки. Разделите список на части до 100 строк.</p>}
              </div>
              <div>
                <label htmlFor="links" className="block text-sm font-medium text-gray-700 mb-2">
                  Ссылки или имена групп — по одной в строке
                </label>
                <textarea
                  id="links"
                  value={links}
                  onChange={(e) => setLinks(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`@mychannel1
t.me/mychannel2
https://t.me/mychannel3
telegram.me/mychannel4`}
                  disabled={addGroupsMutation.isLoading || isPublicBatchProcessing}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={addGroupsMutation.isLoading || isPublicBatchProcessing}
                >
                  Закрыть
                </Button>
                <Button
                  type="submit"
                  icon={PlusIcon}
                  isLoading={addGroupsMutation.isLoading}
                  disabled={addGroupsMutation.isLoading || isPublicBatchProcessing || !links.trim()}
                >
                  {addGroupsMutation.isLoading ? 'Добавление…' : autoProcess ? 'Добавить в автообработку' : 'Добавить группы'}
                </Button>
              </div>
            </form>

            {addGroupsMutation.isLoading && (
              <div className="bg-blue-50 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Добавляем группы. Дождитесь результатов по ссылкам.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Batch Upload Modal */}
      <BatchUploadModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        links={linksArray}
        onComplete={handleBatchComplete}
      />
    </div>
  );
};

export default AddGroupsModal;
